
import { Component, Vue } from 'vue-property-decorator'
import HeadLable from '@/components/HeadLable/index.vue'
import Empty from '@/components/Empty/index.vue'
import {
  getInviteRewardConfigList,
  addInviteRewardConfig,
  updateInviteRewardConfig,
  setInviteRewardConfigEnabled
} from '@/api/inviteReward'
import { getCategoryList } from '@/api/category'
import { listMyStores } from '@/api/store'
import { UserModule } from '@/store/modules/user'

@Component({
  name: 'InviteRewardConfig',
  components: { HeadLable, Empty }
})
export default class extends Vue {
  private tableData: any[] = []
  private dialogVisible = false
  private dialogTitle = '新增配置'
  private storeOptions: Array<{ id: number; name: string }> = []
  private categoryOptions: Array<{ id: number; name: string; label: string }> = []
  private categoryNameMap: Record<string, string> = {}
  private categoryLoading = false
  private form: any = {
    id: null,
    storeId: null as number | null,
    rewardPerBucket: '2',
    bucketCategoryId: null as number | null,
    enabledSwitch: true
  }

  created() {
    this.loadStores()
    this.loadList()
  }

  private getCurrentStoreId(): number | null {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  private get currentStoreLabel(): string {
    const sid = this.getCurrentStoreId()
    if (sid == null) return '未选择商户'
    const found = this.storeOptions.find((s) => Number(s.id) === sid)
    return found ? found.name : `店铺#${sid}`
  }

  private onlyCurrentStoreRows(list: any[]): any[] {
    const sid = this.getCurrentStoreId()
    if (sid == null) return []
    return (list || []).filter(
      (row) => row && row.storeId != null && Number(row.storeId) === sid
    )
  }

  private async loadStores() {
    try {
      const res: any = await listMyStores()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.storeOptions = res.data.data.map((it: any) => ({
          id: Number(it.id),
          name: it.storeName || it.name || `店铺${it.id}`
        }))
      } else {
        this.storeOptions = []
      }
    } catch (e) {
      this.storeOptions = []
    }
  }

  private formatStoreLabel(storeId: any) {
    if (storeId == null || storeId === '') return '（历史全局，已隐藏）'
    const found = this.storeOptions.find((s) => Number(s.id) === Number(storeId))
    return found ? found.name : `店铺#${storeId}`
  }

  private async loadList() {
    try {
      const res = await getInviteRewardConfigList()
      if (String(res.data.code) === '1') {
        this.tableData = this.onlyCurrentStoreRows(res.data.data || [])
      }
      await this.prefetchCategoryLabels()
    } catch (e) {
      console.log(e)
    }
  }

  private resolveCategoryStoreId(): number | null {
    if (this.form.storeId != null && this.form.storeId !== '') {
      const n = Number(this.form.storeId)
      return Number.isNaN(n) ? null : n
    }
    return this.getCurrentStoreId()
  }

  private async resolveAnyStoreId(): Promise<number | null> {
    const direct = this.resolveCategoryStoreId()
    if (direct) return direct
    for (const row of this.tableData) {
      if (row && row.storeId != null && row.storeId !== '') {
        const n = Number(row.storeId)
        if (!Number.isNaN(n)) return n
      }
    }
    try {
      const res: any = await listMyStores()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data) && res.data.data.length) {
        const id = Number(res.data.data[0].id)
        return Number.isNaN(id) ? null : id
      }
    } catch (e) {
      /* ignore */
    }
    return null
  }

  private buildCategoryPathMap(list: any[]) {
    const byId: Record<string, any> = {}
    list.forEach((c: any) => {
      if (c && c.id != null) byId[String(c.id)] = c
    })
    const pathOf = (id: any): string => {
      const parts: string[] = []
      let cur = byId[String(id)]
      let guard = 0
      while (cur && guard < 8) {
        if (cur.name) parts.unshift(String(cur.name))
        if (cur.parentId == null || cur.parentId === 0) break
        cur = byId[String(cur.parentId)]
        guard++
      }
      return parts.join(' / ')
    }
    list.forEach((c: any) => {
      if (c && c.id != null) {
        this.$set(this.categoryNameMap, String(c.id), pathOf(c.id) || String(c.name || c.id))
      }
    })
  }

  private buildChildCategoryOptions(list: any[]) {
    if (!Array.isArray(list)) {
      return []
    }
    const byId: Record<string, any> = {}
    list.forEach((c: any) => {
      if (c && c.id != null) byId[String(c.id)] = c
    })
    const pathOf = (id: any): string => {
      const parts: string[] = []
      let cur = byId[String(id)]
      let guard = 0
      while (cur && guard < 8) {
        if (cur.name) parts.unshift(String(cur.name))
        if (cur.parentId == null || cur.parentId === 0) break
        cur = byId[String(cur.parentId)]
        guard++
      }
      return parts.join(' / ')
    }
    return list
      .filter(
        (c) =>
          c &&
          c.id != null &&
          c.parentId != null &&
          c.parentId !== 0 &&
          (c.type == null || Number(c.type) === 1)
      )
      .map((c: any) => ({
        id: Number(c.id),
        name: c.name,
        label: pathOf(c.id) || String(c.name)
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'))
  }

  private async prefetchCategoryLabels() {
    const storeId = await this.resolveAnyStoreId()
    if (!storeId) return
    try {
      const res: any = await getCategoryList({ storeId, type: 1 })
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.buildCategoryPathMap(res.data.data)
        this.categoryOptions = this.buildChildCategoryOptions(res.data.data)
      }
    } catch (e) {
      /* ignore */
    }
  }

  private formatBucketCategoryLabel(id: any) {
    if (id == null || id === '') return '未指定（名称含「桶」）'
    const key = String(id)
    return this.categoryNameMap[key] || `分类#${id}`
  }

  private onCategorySelectVisible(visible: boolean) {
    if (visible) this.loadCategoryOptions()
  }

  private async loadCategoryOptions() {
    const storeId = await this.resolveAnyStoreId()
    if (!storeId) {
      this.categoryOptions = []
      this.$message.warning('请先选择店铺或在顶栏切换店铺，以便加载分类')
      return
    }
    this.categoryLoading = true
    try {
      const res: any = await getCategoryList({ storeId, type: 1 })
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.buildCategoryPathMap(res.data.data)
        this.categoryOptions = this.buildChildCategoryOptions(res.data.data)
      } else {
        this.categoryOptions = []
      }
    } catch (e) {
      this.categoryOptions = []
    } finally {
      this.categoryLoading = false
    }
  }

  private async openDialog(row?: any) {
    if (!this.storeOptions.length) {
      await this.loadStores()
    }
    const current = this.getCurrentStoreId()
    if (!current) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    if (row) {
      this.dialogTitle = '修改配置'
      this.form = {
        id: row.id,
        storeId: current,
        rewardPerBucket: String(row.rewardPerBucket),
        bucketCategoryId: row.bucketCategoryId != null ? Number(row.bucketCategoryId) : null,
        enabledSwitch: String(row.enabled) === '1'
      }
    } else {
      this.dialogTitle = '新增配置'
      this.form = {
        id: null,
        storeId: current,
        rewardPerBucket: '2',
        bucketCategoryId: null,
        enabledSwitch: true
      }
    }
    this.dialogVisible = true
    await this.loadCategoryOptions()
  }

  private async submit() {
    const storeId = this.getCurrentStoreId()
    if (!storeId) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    const payload: any = {
      rewardPerBucket: Number(this.form.rewardPerBucket),
      enabled: this.form.enabledSwitch ? 1 : 0,
      bucketCategoryId:
        this.form.bucketCategoryId != null && this.form.bucketCategoryId !== ''
          ? Number(this.form.bucketCategoryId)
          : null,
      storeId
    }
    try {
      if (this.form.id) {
        payload.id = this.form.id
        await updateInviteRewardConfig(payload)
      } else {
        await addInviteRewardConfig(payload)
      }
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.loadList()
      this.prefetchCategoryLabels()
    } catch (e) {
      this.$message.error('保存失败')
    }
  }

  private async toggleEnabled(row: any) {
    const enabled = String(row.enabled) === '1' ? 0 : 1
    try {
      await setInviteRewardConfigEnabled(row.id, enabled)
      this.$message.success('操作成功')
      this.loadList()
    } catch (e) {
      this.$message.error('操作失败')
    }
  }
}
